import ConfirmationNumberIcon from '@material-design-icons/svg/filled/confirmation_number.svg?react';
import SettingsIcon from '@material-design-icons/svg/filled/settings.svg?react';
import { use } from 'react';
import { Link } from 'react-router';
import { ProviderSyncStatus } from '../components/common/ProviderSyncStatus';
import { HubspotTicketList } from '../components/hubspot-management/list/HubspotTicketList';
import { HubspotTicketDetailSidebar } from '../components/hubspot-management/panel/HubspotTicketDetailSidebar';
import { HubspotTicketsTable } from '../components/hubspot-management/table/HubspotTicketsTable';
import { HubspotTicketsToolbar } from '../components/hubspot-management/toolbar/HubspotTicketsToolbar';
import { ManagementPageLayout } from '../components/management/ManagementPageLayout';
import { Button } from '../components/ui/button';
import { TRPCContext } from '../contexts/TRPC';
import { AuthStateType } from '../hooks/useAuth';
import { useHubspotColumnVisibility } from '../hooks/hubspot-management/useHubspotColumnVisibility';
import { useHubspotManagementData } from '../hooks/hubspot-management/useHubspotManagementData';
import { useHubspotTicketNavigation } from '../hooks/hubspot-management/useHubspotTicketNavigation';
import { useHubspotUrlSync } from '../hooks/useHubspotUrlSync';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { cn } from '../lib/utils';
import { clearFilters, selectTicket } from '../stores/hubspotManagementStore';
import {
  canAccessHubspotTicketPages,
  hasAdminAccess,
} from '../utils/roleUtils';

export function HubspotManagementPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'hubspotTickets',
  });
  const trpc = use(TRPCContext);

  usePageTitle('HubSpot Tickets | Publishing Portal');

  const hasHydratedUrlState = useHubspotUrlSync();
  const {
    columnVisibility,
    setColumnVisibility,
    handleColumnVisibilityChange,
  } = useHubspotColumnVisibility();
  const {
    tickets,
    selectedTicketId,
    pendingTicketSelection,
    filters,
    sorting,
    pagination,
    isLoading,
    reloadTickets,
    updateFilter,
    setSorting,
    setPage,
    pipelineStageOptions,
    assigneeOptions,
    syncStatus,
  } = useHubspotManagementData({
    authState,
    canRender,
    hasHydratedUrlState,
    trpcClient: trpc.client,
  });
  const { handleNavigate } = useHubspotTicketNavigation({
    tickets,
    selectedTicketId,
    pendingTicketSelection,
    pagination,
  });
  // Below `md` the queue is always a row list; at `xl` an open ticket turns the
  // queue into a narrow navigator. In between, the inspector overlays the page.
  const isNarrow = useMediaQuery('(max-width: 767px)');
  const isSplitLayout = useMediaQuery('(min-width: 1280px)');

  if (!canRender || authState.type !== AuthStateType.Authenticated) {
    return null;
  }

  const canConfigureTickets = hasAdminAccess(
    authState.user.role,
    authState.user.email,
  );
  const isNavigator = selectedTicketId !== null && isSplitLayout;
  const useRowList = isNarrow || isNavigator;
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const reload = () => {
    void reloadTickets();
  };

  return (
    <ManagementPageLayout
      authState={authState}
      canAccess={canAccessHubspotTicketPages}
      className="relative flex min-h-0 flex-1 overflow-hidden"
      wrapContent={false}
    >
      <div
        className={cn(
          'min-w-0 flex-1 overflow-y-auto',
          isNavigator && 'w-[400px] flex-none border-r border-neutral-800',
        )}
      >
        <div
          className={cn(
            'mx-auto w-full max-w-7xl space-y-4 p-4 md:p-6',
            isNavigator && 'space-y-3 p-3 md:p-3',
          )}
        >
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {!isNavigator && (
                <div className="hidden size-10 shrink-0 items-center justify-center rounded-xl bg-neutral-800 sm:flex">
                  <ConfirmationNumberIcon className="size-5 fill-current text-white" />
                </div>
              )}
              <div className="min-w-0">
                <h1
                  className={cn(
                    'font-semibold tracking-tight text-white',
                    isNavigator ? 'text-base' : 'text-xl sm:text-2xl',
                  )}
                >
                  Tickets
                </h1>
                {!isNavigator && (
                  <p className="text-sm text-neutral-400">
                    Review dapp submissions synced from HubSpot.
                  </p>
                )}
              </div>
            </div>

            {canConfigureTickets && (
              <div className="flex items-center gap-2">
                {!isNavigator && (
                  <ProviderSyncStatus
                    syncStatus={syncStatus}
                    providerName="hubspot"
                    className="hidden px-3 py-1 sm:block"
                  />
                )}
                <Button asChild variant="outline" size="sm">
                  <Link to="/tickets/configure" aria-label="Configure tickets">
                    <SettingsIcon className="size-4 fill-current" />
                    {!isNavigator && <span>Configure</span>}
                  </Link>
                </Button>
              </div>
            )}
          </header>

          <HubspotTicketsToolbar
            filters={filters}
            viewerUserId={authState.user.id}
            pipelineStageOptions={pipelineStageOptions}
            assigneeOptions={assigneeOptions}
            sorting={sorting}
            columnVisibility={columnVisibility}
            compact={useRowList}
            showColumnMenu={!useRowList}
            onSearchChange={(value) => updateFilter('search', value)}
            onFilterChange={updateFilter}
            onSortingChange={setSorting}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onClearFilters={clearFilters}
          />

          {useRowList ? (
            <HubspotTicketList
              tickets={tickets}
              pagination={pagination}
              isLoading={isLoading}
              selectedTicketId={selectedTicketId}
              hasActiveFilters={hasActiveFilters}
              onSelectTicket={selectTicket}
              onPageChange={setPage}
              onReload={reload}
              onClearFilters={clearFilters}
            />
          ) : (
            <HubspotTicketsTable
              tickets={tickets}
              sorting={sorting}
              pagination={pagination}
              isLoading={isLoading}
              selectedTicketId={selectedTicketId}
              columnVisibility={columnVisibility}
              hasActiveFilters={hasActiveFilters}
              onReload={reload}
              onSelectTicket={selectTicket}
              onSortingChange={setSorting}
              onColumnVisibilityChange={setColumnVisibility}
              onPageChange={setPage}
              onClearFilters={clearFilters}
            />
          )}
        </div>
      </div>

      <HubspotTicketDetailSidebar
        selectedTicketId={selectedTicketId}
        viewerRole={authState.user.role}
        viewerUserId={authState.user.id}
        tickets={tickets}
        pagination={pagination}
        onClose={() => selectTicket(null)}
        onNavigate={handleNavigate}
        onTicketUpdated={reloadTickets}
      />
    </ManagementPageLayout>
  );
}
